import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnyVariables,
  CombinedError,
  createRequest,
  DocumentInput,
  Operation,
  OperationContext,
  OperationResult,
  UseMutationState,
  UseQueryArgs,
  UseQueryResponse,
  UseQueryState,
  UseSubscriptionArgs,
  UseSubscriptionResponse,
} from 'urql';
import { pipe, subscribe, take, toPromise } from 'wonka';

import { useProductivityClient } from '@/helper/urqlClient/ProductivityClientProvider';

export type {
  SubscriptionHandler,
  UseMutationExecute,
  UseMutationResponse,
  UseQueryArgs,
  UseSubscriptionArgs,
  UseSubscriptionResponse,
} from 'urql';

const getQueryInitialState = <Data = any, Variables extends AnyVariables = AnyVariables>() => {
  return {
    fetching: false,
    stale: false,
  } as UseQueryState<Data, Variables>;
};

const getMutationInitialState = <Data = any, Variables extends AnyVariables = AnyVariables>() => {
  return {
    fetching: false,
    stale: false,
  } as UseMutationState<Data, Variables>;
};

const getSubscriptionInitialState = <
  Data = any,
  Variables extends AnyVariables = AnyVariables,
>() => {
  return {
    fetching: false,
    stale: false,
  } as {
    fetching: boolean;
    stale: boolean;
    data?: Data;
    error?: CombinedError;
    extensions?: Record<string, any>;
    operation?: Operation<Data, Variables>;
  };
};

export const useClient = useProductivityClient;

export function useQuery<Data = any, Variables extends AnyVariables = AnyVariables>(
  args: UseQueryArgs<Variables>,
): UseQueryResponse<Data, Variables> {
  const client = useProductivityClient();
  const [state, setState] = useState<UseQueryState<Data, Variables>>(getQueryInitialState);

  const request = useMemo(
    () =>
      createRequest<Data, Variables>(
        args.query as DocumentInput<Data, Variables>,
        args.variables as Variables,
      ),
    [args.query, args.variables],
  );

  const executeQuery = useCallback(
    (opts?: Partial<OperationContext>) => {
      setState((prev) => ({ ...prev, fetching: true }));

      const operationContext = {
        ...args.context,
        ...opts,
        requestPolicy: opts?.requestPolicy || args.requestPolicy,
      };

      pipe(
        client.query(request.query, request.variables, operationContext),
        take(1),
        subscribe((result) => {
          setState({
            ...result,
            fetching: false,
            stale: !!result.stale,
          });
        }),
      );
    },
    [args.context, args.requestPolicy, client, request.query, request.variables],
  );

  useEffect(() => {
    if (args.pause) {
      setState((prev) => ({ ...prev, fetching: false }));
      return;
    }

    executeQuery();
  }, [args.pause, executeQuery, request.key]);

  return [state, executeQuery];
}

export function useMutation<Data = any, Variables extends AnyVariables = AnyVariables>(
  query: DocumentInput<Data, Variables>,
) {
  const client = useProductivityClient();
  const [state, setState] = useState<UseMutationState<Data, Variables>>(getMutationInitialState);

  const executeMutation = useCallback(
    async (variables: Variables, context?: Partial<OperationContext>) => {
      setState((prev) => ({ ...prev, fetching: true }));

      const result = (await pipe(
        client.mutation(query, variables, context),
        take(1),
        toPromise,
      )) as OperationResult<Data, Variables>;

      setState({
        ...result,
        fetching: false,
        stale: !!result.stale,
      });

      return result;
    },
    [client, query],
  );

  return [state, executeMutation] as const;
}

export function useSubscription<
  Data = any,
  Result = Data,
  Variables extends AnyVariables = AnyVariables,
>(
  args: UseSubscriptionArgs<Variables>,
  handler?: (prev: Result | undefined, data: Data) => Result,
): UseSubscriptionResponse<Result, Variables> {
  const client = useProductivityClient();
  const [state, setState] = useState(getSubscriptionInitialState<Result, Variables>);
  const unsubscribeRef = useRef<undefined | (() => void)>();

  const request = useMemo(
    () =>
      createRequest<Data, Variables>(
        args.query as DocumentInput<Data, Variables>,
        args.variables as Variables,
      ),
    [args.query, args.variables],
  );

  const executeSubscription = useCallback(
    (opts?: Partial<OperationContext>) => {
      unsubscribeRef.current?.();

      const operationContext = {
        ...args.context,
        ...opts,
      };

      setState((prev) => ({ ...prev, fetching: true }));

      const subscription = pipe(
        client.subscription(request.query, request.variables, operationContext),
        subscribe((result) => {
          setState((prev) => ({
            ...result,
            data:
              handler && result.data
                ? handler(prev.data as Result | undefined, result.data as Data)
                : (result.data as Result | undefined),
            fetching: true,
            stale: !!result.stale,
          }));
        }),
      );

      unsubscribeRef.current = subscription.unsubscribe;
    },
    [args.context, client, handler, request.query, request.variables],
  );

  useEffect(() => {
    if (args.pause) {
      unsubscribeRef.current?.();
      setState((prev) => ({ ...prev, fetching: false }));
      return;
    }

    executeSubscription();

    return () => {
      unsubscribeRef.current?.();
    };
  }, [args.pause, executeSubscription, request.key]);

  return [state as any, executeSubscription];
}
